import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Loader2, Wallet, ChevronRight, Download } from 'lucide-react';
import { toast } from 'sonner';
import { accountingAPI } from '@/lib/api';

function money(v) {
  const n = Number(v || 0);
  return `${n < 0 ? '-$' : '$'}${Math.abs(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }); }
  catch { return iso; }
}

const STATUS_TONE = {
  paid:      'bg-emerald-100 text-emerald-800 border-emerald-300',
  failed:    'bg-red-100 text-red-800 border-red-300',
  zero_net:  'bg-slate-200 text-slate-800 border-slate-300',
};

/**
 * Compact card showing the recipient's payment transaction history.
 * Mounted alongside PaymentInformationCard on Agent + Agency profiles.
 */
export default function PaymentHistoryCard({ recipientType, recipientId }) {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState(null);

  const load = useCallback(async () => {
    if (!recipientType || !recipientId) return;
    setLoading(true);
    try {
      const { data } = await accountingAPI.recipientPaymentHistory(recipientType, recipientId, { limit: 50 });
      setRows(data.items || []);
    } catch { /* silent — permissions or empty */ }
    finally { setLoading(false); }
  }, [recipientType, recipientId]);

  useEffect(() => { load(); }, [load]);

  const handleDownloadPdf = async (row, e) => {
    e.stopPropagation();
    if (!row?.statement_id) return;
    setDownloadingId(row.transaction_id);
    try {
      const res = await accountingAPI.downloadStatementPdf(row.statement_id);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${row.statement_number || row.statement_id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 404) toast.error('PDF not yet available for this statement');
      else if (status === 403) toast.error('You are not authorized to download this PDF');
      else toast.error('Download failed');
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <Card data-testid="payment-history-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Wallet className="h-4 w-4" /> Payment History
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {loading ? (
          <div className="p-6 text-slate-500 flex items-center gap-2 text-sm">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : rows.length === 0 ? (
          <div className="p-6 text-slate-500 text-sm">No payments recorded yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200 text-slate-600">
                <tr>
                  <th className="text-left px-3 py-2 font-medium">Date</th>
                  <th className="text-left px-3 py-2 font-medium">Batch</th>
                  <th className="text-right px-3 py-2 font-medium">Amount</th>
                  <th className="text-left px-3 py-2 font-medium">Method</th>
                  <th className="text-left px-3 py-2 font-medium">Reference</th>
                  <th className="text-left px-3 py-2 font-medium">Status</th>
                  <th className="text-center px-2 py-2 font-medium">PDF</th>
                  <th className="w-6"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((t) => (
                  <tr key={t.transaction_id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50 cursor-pointer"
                      onClick={() => t.batch_id && navigate(`/accounting/payment-batches/${t.batch_id}`)}
                      data-testid={`ph-row-${t.transaction_id}`}>
                    <td className="px-3 py-2 text-slate-700">{fmtDate(t.payment_date || t.executed_at)}</td>
                    <td className="px-3 py-2 font-mono text-xs">{t.batch_number || '—'}</td>
                    <td className="px-3 py-2 text-right font-medium">{money(t.amount)}</td>
                    <td className="px-3 py-2 uppercase text-xs">{t.method || '—'}</td>
                    <td className="px-3 py-2 font-mono text-xs">{t.reference || '—'}</td>
                    <td className="px-3 py-2">
                      <Badge className={`${STATUS_TONE[t.status] || 'bg-slate-100 text-slate-700 border-slate-300'} border font-normal`}>
                        {(t.status || '').replace('_', '-').toUpperCase()}
                      </Badge>
                      {t.status === 'failed' && t.reason_code && (
                        <div className="text-[10px] text-red-700 italic mt-0.5">{t.reason_code.replace(/_/g, ' ')}</div>
                      )}
                    </td>
                    <td className="px-2 py-2 text-center">
                      {t.status === 'paid' && t.statement_id ? (
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          className="h-7 w-7 p-0"
                          onClick={(e) => handleDownloadPdf(t, e)}
                          disabled={downloadingId === t.transaction_id}
                          data-testid={`ph-pdf-${t.transaction_id}`}
                          title={`Download statement ${t.statement_number || ''} PDF`}
                        >
                          {downloadingId === t.transaction_id
                            ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            : <Download className="h-3.5 w-3.5" />}
                        </Button>
                      ) : (
                        <span className="text-slate-300 text-xs">—</span>
                      )}
                    </td>
                    <td className="px-2 py-2 text-slate-300"><ChevronRight className="h-3.5 w-3.5" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
